import React, { useEffect } from 'react';

interface ToastProps {
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
    isVisible: boolean;
    onClose: () => void;
    duration?: number;
}

const Toast: React.FC<ToastProps> = ({
    message,
    type,
    isVisible,
    onClose,
    duration = 10000
}) => {
    useEffect(() => {
        if (isVisible && duration > 0) {
            const timer = setTimeout(() => {
                onClose();
            }, duration);

            return () => clearTimeout(timer);
        }
    }, [isVisible, onClose, duration]);

    if (!isVisible) return null;

    const getToastStyles = () => {
        const baseStyles = {
            position: 'fixed' as const,
            top: '20px',
            right: '20px',
            zIndex: 9999,
            padding: '16px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            maxWidth: '400px',
            minWidth: '300px',
            fontSize: '14px',
            fontWeight: '500',
            animation: 'slideInFromRight 0.3s ease-out',
            border: '1px solid',
        };

        const typeStyles = {
            success: {
                backgroundColor: '#d4edda',
                borderColor: '#c3e6cb',
                color: '#155724',
            },
            error: {
                backgroundColor: '#f8d7da',
                borderColor: '#f5c6cb',
                color: '#721c24',
            },
            warning: {
                backgroundColor: '#fff3cd',
                borderColor: '#ffeaa7',
                color: '#856404',
            },
            info: {
                backgroundColor: '#d1ecf1',
                borderColor: '#bee5eb',
                color: '#0c5460',
            },
        };

        return { ...baseStyles, ...typeStyles[type] };
    };

    const getIcon = () => {
        const iconStyles = {
            fontSize: '16px',
            fontWeight: 'bold',
        };

        switch (type) {
            case 'success':
                return <span style={{ ...iconStyles, color: '#28a745' }}>✓</span>;
            case 'error':
                return <span style={{ ...iconStyles, color: '#dc3545' }}>✕</span>;
            case 'warning':
                return <span style={{ ...iconStyles, color: '#ffc107' }}>⚠</span>;
            case 'info':
                return <span style={{ ...iconStyles, color: '#17a2b8' }}>ℹ</span>;
            default:
                return null;
        }
    };

    return (
        <>
            <style>
                {`
          @keyframes slideInFromRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes fadeOut {
            from {
              opacity: 1;
              transform: translateX(0);
            }
            to {
              opacity: 0;
              transform: translateX(100%);
            }
          }
        `}
            </style>
            <div style={getToastStyles()}>
                {getIcon()}
                <span style={{ flex: 1 }}>{message}</span>
                <button
                    onClick={onClose}
                    style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '18px',
                        cursor: 'pointer',
                        color: 'inherit',
                        opacity: 0.7,
                        padding: '4px',
                        lineHeight: 1,
                        borderRadius: '4px',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.opacity = '0.7';
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.transform = 'scale(1)';
                    }}
                    onMouseDown={(e) => {
                        e.currentTarget.style.transform = 'scale(0.95)';
                    }}
                    onMouseUp={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                >
                    ×
                </button>
            </div>
        </>
    );
};

export default Toast;